import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from '@mui/material';
import { PRIMARY_BTN_SX } from '../../styles/designSystem';
import { FormField } from '../panel/FormField';
import { useCreateCustomer } from '../../hooks/useCreateCustomer';
import type { EditableCustomerUpdates } from '../../services/customerUpdateApi';
import type { CustomerCreateResult } from '../../services/customerCreateApi';

interface QuickNewCustomerDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (result: CustomerCreateResult) => void;
}

interface FormState {
  firstName: string;
  lastName: string;
  businessName: string;
  mobile: string;
  phone: string;
  email: string;
}

const EMPTY_FORM: FormState = {
  firstName: '',
  lastName: '',
  businessName: '',
  mobile: '',
  phone: '',
  email: '',
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const QuickNewCustomerDialog: React.FC<QuickNewCustomerDialogProps> = ({
  open,
  onClose,
  onCreated,
}) => {
  const { isSaving, error: apiError, submit, reset } = useCreateCustomer();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [touched, setTouched] = useState(false);

  const handleField = (key: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      setForm((prev) => ({ ...prev, [key]: value }));
    };

  const trimmed = {
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim(),
    businessName: form.businessName.trim(),
    mobile: form.mobile.trim(),
    phone: form.phone.trim(),
    email: form.email.trim(),
  };

  const firstNameError = !trimmed.firstName;
  const lastNameError = !trimmed.lastName;
  const phonePairError = !trimmed.mobile && !trimmed.phone;
  const emailError = !!trimmed.email && !EMAIL_REGEX.test(trimmed.email);
  const hasError = firstNameError || lastNameError || phonePairError || emailError;

  const handleClose = () => {
    if (isSaving) return;
    setForm(EMPTY_FORM);
    setTouched(false);
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    setTouched(true);
    if (hasError) return;
    const payload: EditableCustomerUpdates = {
      firstName: trimmed.firstName,
      lastName: trimmed.lastName,
      fullName: `${trimmed.firstName} ${trimmed.lastName}`.trim(),
      businessName: trimmed.businessName,
      mobile: trimmed.mobile,
      phone: trimmed.phone,
      email: trimmed.email,
    };
    try {
      const result = await submit(payload);
      setForm(EMPTY_FORM);
      setTouched(false);
      onCreated(result);
    } catch {
      // error surfaced via apiError
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Customer</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {apiError && <Alert severity="error">{apiError}</Alert>}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <FormField label="First Name" error={touched && firstNameError ? 'Required' : undefined}>
              <input
                value={form.firstName}
                onChange={handleField('firstName')}
                autoFocus
                disabled={isSaving}
              />
            </FormField>
            <FormField label="Last Name" error={touched && lastNameError ? 'Required' : undefined}>
              <input
                value={form.lastName}
                onChange={handleField('lastName')}
                disabled={isSaving}
              />
            </FormField>
          </Box>
          <FormField label="Business (Optional)">
            <input
              value={form.businessName}
              onChange={handleField('businessName')}
              disabled={isSaving}
            />
          </FormField>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <FormField
              label="Mobile Number"
              error={touched && phonePairError ? 'Mobile or Phone required' : undefined}
            >
              <input
                value={form.mobile}
                onChange={handleField('mobile')}
                disabled={isSaving}
              />
            </FormField>
            <FormField
              label="Phone Number"
              error={touched && phonePairError ? 'Mobile or Phone required' : undefined}
            >
              <input
                value={form.phone}
                onChange={handleField('phone')}
                disabled={isSaving}
              />
            </FormField>
          </Box>
          <FormField label="Email Address" error={touched && emailError ? 'Invalid email format' : undefined}>
            <input
              type="email"
              value={form.email}
              onChange={handleField('email')}
              disabled={isSaving}
            />
          </FormField>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          disabled={isSaving}
          sx={{ color: '#aaaaaa', '&:hover': { color: '#555555', bgcolor: 'transparent' } }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSaving || (touched && hasError)}
          startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={PRIMARY_BTN_SX}
        >
          Create Customer
        </Button>
      </DialogActions>
    </Dialog>
  );
};
